/* START-USER-IMPORTS */
import { GraphemeResponse } from '@/common/types/assessment';
import { Word, WordId } from '@/common/types/scopeAndSequence';
import { CreateAttempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import {
  leafyMakeTheWordAudio,
  playWordDefinition,
  playWordInSentence,
  stopAudio,
} from '@/common/utils/audio';
import { getDistractorGraphemes } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getWordGraphemes } from '@/common/utils/scopeAndSequence';
import api from '@/db/apiClient';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { StudentGameInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameInputHandler';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import SettingsPanel from '@/game/GameComponents/PhaserGame/Scenes/Settings/SettingsPanel';
import { SettingsPanelController } from '@/game/GameComponents/PhaserGame/Scenes/Settings/SettingsPanelController';
import { DEFAULT_SCALE_DOWN_CONFIG } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/ResponsivenessManagerTypes';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  getAnchorPlugin,
  isPhaserDevMode,
  waitForMilliseconds,
  waitForSeconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { getAssessmentSettingsConfig } from '@/game/GameComponents/PhaserGame/Utils/SettingsPanelConfigs';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import {
  getHighestLevelForPhase,
  getNextAssessmentWord,
  getPlacementLevel,
  getScreenerWords,
  getUpdatedGraphemeResponses,
} from '@/game/prefabs/Assessment/assessmentHelpers';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import PlayerTypingInput from '@/game/prefabs/StudentGame/PlayerTypingInput';
import SequencedCardDropSite from '@/game/prefabs/StudentGame/SequencedCardDropSite';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import QuitToMenuModal from '@/game/prefabs/WorldUI/QuitToMenuModal';
import AssessmentUI from '@/game/scenes/Assessment/AssessmentUI';
import AssessmentWalkthroughPanel from '@/game/scenes/Assessment/AssessmentWalkthroughPanel';

export class AssessmentUIController {
  constructor(private assessmentUI: AssessmentUI) {}

  private _currentWord: Word | null = null;
  get currentWord() {
    return this._currentWord;
  }
  private _screenerPhase: number | null = null;
  get screenerPhase() {
    return this._screenerPhase;
  }

  private usedWords: WordId[] = [];
  private graphemeResponses: GraphemeResponse[] = [];

  boardUpdateTween?: Phaser.Tweens.Tween;
  boardCardTweens: Phaser.Tweens.Tween[] = [];
  handUpdateTween?: Phaser.Tweens.Tween;
  onAssessmentComplete?: () => void;
  onInstructionsComplete?: () => void;
  static controlScheme: ControllerType = ControllerType.KEYBOARD;

  getResponsiveElements(key: keyof AssessmentUI) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene(
      this.assessmentUI,
      key,
    );
  }

  getResponsiveElementsArray(keys: (keyof AssessmentUI)[]) {
    return keys.map((key) => this.getResponsiveElements(key));
  }

  initialize() {
    const { assessmentUI } = this;
    assessmentUI.inputHandler = new StudentGameInputHandler(assessmentUI);
    // Input handler gets the student's controller type by default,
    // but the assessment can override it.
    assessmentUI.inputHandler.updateCurrentInputSystem(
      assessmentUI.getControlScheme(),
    );
    AssessmentUIController.controlScheme = assessmentUI.getControlScheme();
    assessmentUI.switchControlsModal.assessmentUI = assessmentUI;

    this.getResponsiveElements('keyboardPrompt').forEach(
      (keyboardPrompt: Phaser.GameObjects.Container) => {
        keyboardPrompt.setVisible(false);
      },
    );

    this.getResponsiveElements('clickPrompt').forEach(
      (clickPrompt: Phaser.GameObjects.Container) => {
        clickPrompt.setVisible(false);
      },
    );

    this.getResponsiveElements('helpContainer').forEach(
      (helpContainer: Phaser.GameObjects.Container) => {
        helpContainer.setVisible(false);
      },
    );

    this.getResponsiveElements('definitionExampleView').forEach(
      (definitionExampleView: Phaser.GameObjects.Container) => {
        definitionExampleView.setVisible(false);
      },
    );

    this.getResponsiveElements('quitToMenuModal').forEach(
      (quitToMenuModal: Phaser.GameObjects.Container) => {
        quitToMenuModal.setVisible(false);
      },
    );

    this.getResponsiveElements('autocompleteAssessmentButton').forEach(
      (autocompleteAssessmentButton: HUDButton) => {
        autocompleteAssessmentButton.setVisible(isPhaserDevMode(getStudent()));
        autocompleteAssessmentButton.setSubmissionCallback(() => {
          this.finishAssessment();
        });
      },
    );

    this.getResponsiveElements('repeatInstructionButton').forEach(
      (repeatInstructionButton: HUDButton) => {
        repeatInstructionButton.setSubmissionCallback(() => {
          this.playCurrentWordAudio();
        });
      },
    );

    this.getResponsiveElements('playDefinitionButton').forEach(
      (playDefinitionButton: HUDButton) => {
        playDefinitionButton.setSubmissionCallback(() => {
          this.playCurrentWordDefinition();
        });
      },
    );

    this.getResponsiveElements('playWordInSentenceButton').forEach(
      (playWordInSentenceButton: HUDButton) => {
        playWordInSentenceButton.setSubmissionCallback(() => {
          this.playCurrentWordInSentence();
        });
      },
    );

    this.getResponsiveElements('startButton1').forEach(
      (startButton1: DefaultButton) => {
        startButton1.controller.setSubmissionCallback(() => {
          this.instructionsComplete();
        });
      },
    );

    this.getResponsiveElements('startButton2').forEach(
      (startButton2: DefaultButton) => {
        startButton2.controller.setSubmissionCallback(() => {
          this.instructionsComplete();
        });
      },
    );

    this.getResponsiveElements('submitButton').forEach(
      (submitButton: DefaultButton) => {
        submitButton.controller.setSubmissionCallback(() => {
          this.checkAnswer();
        });
      },
    );

    this.getResponsiveElements('settingsButton').forEach(
      (submitButton: HUDButton) => {
        submitButton.setSubmissionCallback(() => {
          SettingsPanelController.openWithConfiguration(
            getAssessmentSettingsConfig(getStudent()),
          );
        });
      },
    );

    this.getResponsiveElements('switchControlsButton').forEach(
      (switchControlsButton: HUDButton) => {
        switchControlsButton.setSubmissionCallback(() => {
          const scheme = assessmentUI.getControlScheme();
          if (assessmentUI.switchControlsModal.visible) {
            assessmentUI.switchControlsModal.setVisible(false);
          } else {
            assessmentUI.switchControlsModal.openForControllerType(scheme);
          }
        });
      },
    );

    this.getResponsiveElements('switchContextButton').forEach(
      (switchContextButton: DefaultButton) => {
        switchContextButton.controller.setSubmissionCallback(() => {
          if (
            assessmentUI.getControlScheme() === ControllerType.CLICK ||
            assessmentUI.getControlScheme() === ControllerType.DRAG
          ) {
            this.initiateKeyboardOverride();
          } else {
            this.initiateLetterCardOverride();
          }
        });
      },
    );

    this.getResponsiveElements('helpButton').forEach(
      (helpButton: HUDButton) => {
        helpButton.setSubmissionCallback(() => {
          // Do help walkthrough.
          getSceneManager()?.launchSubScene(
            'Assessment',
            'AssessmentWalkthroughPanel',
            AssessmentWalkthroughPanel,
            'ui',
          );
          const assessmentWalkthrough = assessmentUI.scene.get(
            'AssessmentWalkthroughPanel',
          ) as AssessmentWalkthroughPanel;
          if (assessmentWalkthrough) {
            assessmentWalkthrough.reset();
          }
        });
      },
    );

    this.getResponsiveElements('quitButton').forEach(
      (quitButton: HUDButton) => {
        quitButton.setSubmissionCallback(() => {
          this.getResponsiveElements('quitToMenuModal').forEach(
            (quitToMenuModal: QuitToMenuModal) => {
              quitToMenuModal.open();
            },
          );
        });
      },
    );

    this.setupControlScheme();

    this.initializeResponsiveness();

    EventsManager.registerEventOn(
      assessmentUI,
      GAME_EVENTS.STUDENT_GAME_KEYBOARD_INPUT,
      this.handleStudentGameKeyboardInput,
      this,
    );
  }

  setupControlScheme() {
    const scheme = this.assessmentUI.getControlScheme();
    this.getResponsiveElements('typingView').forEach(
      (typingView: Phaser.GameObjects.Container) => {
        typingView.setVisible(scheme === ControllerType.KEYBOARD);
      },
    );

    this.getResponsiveElements('handArea').forEach(
      (handArea: Phaser.GameObjects.Container) => {
        handArea.setVisible(
          scheme === ControllerType.CLICK || scheme === ControllerType.DRAG,
        );
      },
    );

    this.getResponsiveElements('cardView').forEach(
      (cardView: Phaser.GameObjects.Container) => {
        cardView.setVisible(
          scheme === ControllerType.CLICK || scheme === ControllerType.DRAG,
        );
      },
    );
  }

  async doSubmissionFeedback() {
    const feedbackDuration = 400;

    this.getResponsiveElements('boardDropSite').forEach(
      (boardDropSite: SequencedCardDropSite) => {
        boardDropSite.layoutGroup?.getMinigameCards().forEach(async (card) => {
          await card.showPurpleFeedback(feedbackDuration, true);
          card.doFadeOut(feedbackDuration);
        });
      },
    );

    this.getResponsiveElements('handDropSite').forEach(
      (handDropSite: SequencedCardDropSite) => {
        handDropSite.layoutGroup?.getMinigameCards().forEach((card) => {
          waitForMilliseconds(this.assessmentUI.time, feedbackDuration).then(
            () => {
              card.doFadeOut(feedbackDuration);
            },
          );
        });
      },
    );

    this.getResponsiveElements('playerTypingInput').forEach(
      (typingInput: PlayerTypingInput) => {
        typingInput.getCharacterArray().forEach((character) => {
          character.doFadeOutFeedback();
        });
      },
    );

    await waitForSeconds(this.assessmentUI.time, 0.5);
    await this.assessmentUI.clearTypedInput();
  }

  async startAssessment() {
    const student = getStudent();
    const studentId = student.id;
    const assessmentId = student.assessmentId;
    if (!assessmentId) {
      console.error('No assessment to start');
      return;
    }

    GameInstance.instance.updateGeneralGameState({
      currentGame: 'assessment',
    });
    await GameInstance.instance.startActivity('assessment');
    api.asStudent.markAssessmentStarted(assessmentId, studentId).catch((e) => {
      console.error('Error marking assessment started:', e);
    });

    this.setPromptsVisible(false);
    this.setBoardInteractive(false);
    this.setupControlScheme();
    this.testNextWord();
  }

  setPromptsVisible(visible: boolean) {
    this.getResponsiveElements('keyboardPrompt').forEach(
      (keyboardPrompt: Phaser.GameObjects.Container) => {
        keyboardPrompt.setVisible(visible);
      },
    );

    this.getResponsiveElements('clickPrompt').forEach(
      (clickPrompt: Phaser.GameObjects.Container) => {
        clickPrompt.setVisible(visible);
      },
    );
  }

  instructionsComplete() {
    this.hideInstructions();
    this.onInstructionsComplete?.();
  }

  hideInstructions() {
    this.setPromptsVisible(false);
  }

  async testNextWord() {
    const { assessmentUI } = this;
    const doScreener = assessmentUI.getAssessmentStudent().doScreener;

    // Check if we can determine placement (2+ mistakes in a phase)
    if (doScreener && !this._screenerPhase) {
      this.checkScreenerPlacement();
    }

    const screenerComplete = !!this._screenerPhase;
    const nextWord = getNextAssessmentWord(
      assessmentUI.getPhase(),
      doScreener,
      screenerComplete,
      this.usedWords,
    );
    this._currentWord = nextWord;

    if (!nextWord) {
      this.finishAssessment();
      return;
    }

    this.usedWords.push(nextWord.id);

    if (
      assessmentUI.getControlScheme() === ControllerType.CLICK ||
      assessmentUI.getControlScheme() === ControllerType.DRAG
    ) {
      this.getNewHand();
    }

    GameInstance.instance.resetCurrentAttemptStartedAt();
    this.setBoardInteractive(true);
    await waitForSeconds(assessmentUI.time, 0.5);
    await this.playCurrentWordAudio();
    GameInstance.instance.resetCurrentAttemptStartedAt();
    EventBus.emit(GAME_EVENTS.STUDENT_GAME_ROUND_STARTED);
  }

  checkScreenerPlacement() {
    const countMistakes = (acc: number, r: GraphemeResponse) =>
      acc + r.timesAttempted - r.timesCorrect;

    const getMistakesForPhase = (phase: number) =>
      this.graphemeResponses
        .filter((r) => r.phaseFirstIntroduced === phase)
        .reduce(countMistakes, 0);

    if (getMistakesForPhase(1) >= 2) {
      this._screenerPhase = 1;
    } else if (getMistakesForPhase(2) >= 2) {
      this._screenerPhase = 2;
    } else if (this.usedWords.length === getScreenerWords().length) {
      this._screenerPhase = 3;
    }
  }

  async playCurrentWordAudio() {
    if (!this._currentWord) return;
    stopAudio();
    if (this.usedWords.length === 0) {
      await leafyMakeTheWordAudio();
    }
    try {
      await playWordInSentence(this._currentWord);
    } catch (error) {
      console.error('Error playing word audio', error);
    }
  }

  setBoardInteractive(isInteractive: boolean) {
    this.assessmentUI.boardInteractive = isInteractive;
    this.getAllCards().forEach((card) => {
      card.makeInteractive(isInteractive, false);
    });

    this.getResponsiveElements('submitButton').forEach(
      (submitButton: DefaultButton) => {
        submitButton.controller.makeInteractive(
          isInteractive ? this.assessmentUI.getCanSubmit() : false,
        );
      },
    );

    this.getResponsiveElementsArray([
      'repeatInstructionButton',
      'playDefinitionButton',
      'playWordInSentenceButton',
      'helpButton',
      'quitButton',
      'settingsButton',
      'switchControlsButton',
    ]).forEach((responsiveElement: any[]) => {
      responsiveElement.forEach((element: any) => {
        element.setInteractionEnabled(isInteractive);
      });
    });
    // this.repeatInstructionButton.setInteractionEnabled(isInteractive);
    // this.playDefinitionButton.setInteractionEnabled(isInteractive);
    // this.playWordInSentenceButton.setInteractionEnabled(isInteractive);
    // this.helpButton.setInteractionEnabled(isInteractive);
    // this.quitButton.setInteractionEnabled(isInteractive);
    // this.settingsButton.setInteractionEnabled(isInteractive);
    // this.switchControlsButton.setInteractionEnabled(isInteractive);
  }

  resetBoard() {
    // Destroy all cards.
    this.getResponsiveElementsArray(['boardDropSite', 'handDropSite']).forEach(
      (responsiveDropSite: SequencedCardDropSite[]) => {
        responsiveDropSite.forEach((dropSite: SequencedCardDropSite) => {
          dropSite.layoutGroup?.reset();
        });
      },
    );
  }

  initializeNewAssessmentCard(cardText: string) {
    const { assessmentUI } = this;
    // Create a card in each view.
    this.getResponsiveElements('handDropSite').forEach(
      (hand: SequencedCardDropSite) => {
        const newCard = new StudentGameCard(assessmentUI, 0, 0);
        newCard.setCardText(cardText);
        this.assessmentUI.inputHandler?.initializeCardInput(newCard);
        newCard.origin = {
          dropSite: hand,
          index: hand.layoutGroup?.cards.indexOf(newCard) ?? 0,
        };
        hand.layoutGroup?.pushCard(newCard);
      },
    );
  }

  // Deliberately excludes tablet view.
  getCurrentView() {
    const globalView = ResponsivenessManager.GetCurrentGlobalViewForGame(
      this.assessmentUI,
    );
    return globalView === 'tablet' ? 'desktop' : globalView;
  }

  getNewHand() {
    const nextWord = this._currentWord;
    if (!nextWord) return;

    // Get graphemes for current word
    const wordGraphemes = getWordGraphemes(nextWord);

    // Get distractors
    const highestLevelForPhase = getHighestLevelForPhase(
      this.assessmentUI.getPhase(),
    );
    const DISTRACTOR_COUNT = 3;
    const distractorLetters = getDistractorGraphemes(
      wordGraphemes,
      highestLevelForPhase,
      DISTRACTOR_COUNT,
    );

    // Combing distractors with word and shuffle
    const shuffledWordWithDistractors = [
      ...wordGraphemes.map((g) => g.spelling),
      ...distractorLetters.map((g) => g.spelling),
    ].sort(() => Math.random() - 0.5);

    // Create minigame cards for each letter
    shuffledWordWithDistractors.forEach((word) =>
      this.initializeNewAssessmentCard(word),
    );
  }

  getAllCards() {
    const { assessmentUI } = this;
    return [
      ...(assessmentUI.boardDropSite.layoutGroup?.getMinigameCards() ?? []),
      ...(assessmentUI.handDropSite.layoutGroup?.getMinigameCards() ?? []),
      ...(assessmentUI.boardDropSite_m.layoutGroup?.getMinigameCards() ?? []),
      ...(assessmentUI.handDropSite_m.layoutGroup?.getMinigameCards() ?? []),
    ];
  }

  initiateLetterCardOverride() {
    this.assessmentUI.controlSchemeOverride = ControllerType.CLICK;
    this.assessmentUI.inputHandler?.updateCurrentInputSystem(
      ControllerType.CLICK,
    );
    this.setupControlScheme();
    this.resetBoard();
    this.getNewHand();
    this.setBoardInteractive(true);
    this.getResponsiveElements('helpContainer').forEach(
      (helpContainer: Phaser.GameObjects.Container) => {
        helpContainer.setVisible(false);
      },
    );
    EventBus.emit(
      GAME_EVENTS.INPUT_METHOD_CHANGED,
      this.assessmentUI.controlSchemeOverride,
    );
  }

  initiateKeyboardOverride() {
    this.assessmentUI.controlSchemeOverride = ControllerType.KEYBOARD;
    this.assessmentUI.inputHandler?.updateCurrentInputSystem(
      ControllerType.KEYBOARD,
    );
    this.setupControlScheme();
    this.resetBoard();
    this.setBoardInteractive(true);
    this.getResponsiveElements('helpContainer').forEach(
      (helpContainer: Phaser.GameObjects.Container) => {
        helpContainer.setVisible(false);
      },
    );
  }

  async playCurrentWordDefinition() {
    if (!this._currentWord) return;
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Definition button clicked', {
        word: this._currentWord.spelling,
      });
    }
    this.assessmentUI.showDefinitionForWord(this._currentWord);
    await playWordDefinition(this._currentWord);
  }

  async playCurrentWordInSentence() {
    if (!this._currentWord) return;
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Example button clicked', {
        word: this._currentWord.spelling,
      });
    }
    this.assessmentUI.showExampleForWord(this._currentWord);
    await playWordInSentence(this._currentWord);
  }

  async checkAnswer() {
    const { assessmentUI } = this;
    stopAudio();
    PhaserAudioManager.playSFX(assessmentUI, 'correct-sfx');

    const correctWord = this._currentWord;
    if (!correctWord) {
      console.error('No current word');
      return;
    }

    const currentAttempt = assessmentUI.getInput();
    if (currentAttempt.length === 0) return;

    this.graphemeResponses = getUpdatedGraphemeResponses(
      this.graphemeResponses,
      correctWord,
      currentAttempt,
    );

    // Save attempt
    const student = getStudent();
    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity');
      return;
    }
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'assessment',
      studentAnswer: currentAttempt,
      cardsPlayed: assessmentUI.getBoardCards().map((card) => card.cardText),
      correctAnswer: correctWord.spelling,
      correctAnswerId: correctWord.id,
      control: assessmentUI.getControlScheme(),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    // Update accuracy
    GameInstance.instance.updateActivityAccuracy(
      correctWord.spelling === currentAttempt,
    );

    this.setBoardInteractive(false);

    await this.doSubmissionFeedback();

    this.resetBoard();
    await waitForSeconds(assessmentUI.time, 1);
    this.testNextWord();
  }

  handleSettingsPanelOpened() {
    getSceneManager()?.launchOverlayScene(
      this.assessmentUI,
      'SettingsPanel',
      SettingsPanel,
    );
  }

  handleStudentGameKeyboardInput() {
    this.getResponsiveElements('definitionExampleView').forEach(
      (definitionExampleView: Phaser.GameObjects.Container) => {
        definitionExampleView.setVisible(false);
        this.assessmentUI.tweens.killTweensOf(definitionExampleView);
      },
    );
  }

  finishAssessment() {
    const placementLevel = getPlacementLevel(
      this.assessmentUI.getPhase(),
      this.graphemeResponses,
    );

    GameInstance.instance.endActivity();
    this.onAssessmentComplete?.();
    EventBus.emit(GAME_EVENTS.COMPLETED_ASSESSMENT, placementLevel);
  }

  initializeResponsiveness() {
    const { assessmentUI } = this;
    // Breakpoints.
    // ResponsivenessManager.InitializeResponsiveBreakpoints({
    //   scene: assessmentUI,
    //   mobileView: assessmentUI.mobileView,
    //   desktopView: assessmentUI.desktopView,
    // });

    // Desktop responsiveness.
    const anchor = getAnchorPlugin(assessmentUI);
    anchor.add(assessmentUI.assessmentOptionsContainer, {
      right: '100%',
    });
    anchor.add(assessmentUI.visualBacking, {
      width: '100%',
      height: '100%',
    });
    anchor.add(assessmentUI.keyboardContainer, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(assessmentUI.switchContainer, {
      left: '100%',
      bottom: '100%',
    });
    anchor.add(assessmentUI.switchContainer_1, {
      left: '100%',
      bottom: '100%',
    });
    anchor.add(assessmentUI.clickContainer, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(assessmentUI.visualBacking_1, {
      width: '100%',
      height: '100%',
    });
    anchor.add(assessmentUI.handArea, {
      bottom: '100%',
      centerX: '50%',
    });
    anchor.add(assessmentUI.boardArea, {
      bottom: '100%',
      centerX: '50%',
    });
    anchor.add(assessmentUI.userButtons, {
      bottom: '100%',
      centerX: '50%',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.handArea,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.boardArea,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.userButtons,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.assessmentOptionsContainer,
      { ...DEFAULT_SCALE_DOWN_CONFIG, scaleModifier: 2 },
    );

    // Mobile responsiveness.
    anchor.add(assessmentUI.assessmentOptionsContainer_m, {
      right: '100%',
    });
    anchor.add(assessmentUI.visualBacking_m, {
      width: '100%',
      height: '100%',
    });
    anchor.add(assessmentUI.keyboardContainer_m, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(assessmentUI.switchContainer_m, {
      left: '100%',
      bottom: '100%',
    });
    anchor.add(assessmentUI.switchContainer_1_m, {
      left: '100%',
      bottom: '100%',
    });
    anchor.add(assessmentUI.clickContainer_m, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(assessmentUI.visualBacking_1_m, {
      width: '100%',
      height: '100%',
    });
    anchor.add(assessmentUI.handArea_m, {
      bottom: '100%',
      centerX: '50%',
    });
    anchor.add(assessmentUI.boardArea_m, {
      bottom: '100%',
      centerX: '50%',
    });
    anchor.add(assessmentUI.userButtons_m, {
      bottom: '100%',
      centerX: '50%',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.handArea_m,
      { scaleModifier: 0.75 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.boardArea_m,
      { scaleModifier: 0.75 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.userButtons_m,
      { scaleModifier: 0.75 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.assessmentOptionsContainer_m,
      { scaleModifier: 1.5 },
    );

    ResponsivenessManager.InitializeResizeHandler(
      assessmentUI,
      () => this.handleResize(),
      this,
    );
  }

  handleResize() {
    if (!this.assessmentUI.scene || !this.assessmentUI.scene.isActive()) return;
    const currentView = ResponsivenessManager.GetCurrentGlobalViewForGame(
      this.assessmentUI,
    );
    switch (currentView) {
      case 'desktop':
        this.responsivenessDesktop();
        break;
      case 'mobile':
        this.responsivenessMobile();
        break;
    }
  }

  responsivenessDesktop() {
    const { assessmentUI } = this;
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.handArea,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.boardArea,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.userButtons,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.assessmentOptionsContainer,
      { scaleModifier: 2 },
    );
  }

  responsivenessMobile() {
    const { assessmentUI } = this;
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.handArea,
      { scaleModifier: 0.5 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.boardArea,
      { scaleModifier: 0.5 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.userButtons,
      { scaleModifier: 0.5 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      assessmentUI.assessmentOptionsContainer,
      { scaleModifier: 1 },
    );
  }
}
